/**
 * Workflow Versions v2 — pure reducer.
 *
 * The hook layer (`useWorkflowVersions`) owns all async + lifecycle
 * effects (initial fetch, lazy detail fetch on expand, restore POST,
 * refetch on workflow change) and dispatches into here. The reducer
 * is the projection: list + index + UI selection.
 */
import {
  initialVersionsState,
  VersionsAction,
  VersionsState,
  VersionRecord,
} from './types';

const withVersions = (
  state: VersionsState,
  versions: VersionRecord[],
): VersionsState => {
  const byNumber: Record<number, VersionRecord> = {};
  for (const v of versions) byNumber[v.version_number] = v;
  return { ...state, versions, byNumber };
};

export function versionsReducer(
  state: VersionsState,
  action: VersionsAction,
): VersionsState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_SUCCESS': {
      const { response, append } = action.payload;
      const merged = append
        ? [...state.versions, ...response.items]
        : response.items;
      return {
        ...withVersions(state, merged),
        loading: false,
        error: null,
        pagination: {
          offset: response.offset,
          limit: response.limit,
          total: response.total,
          currentVersionNumber: response.current_version_number,
        },
      };
    }

    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'TOGGLE_EXPAND': {
      const next = new Set(state.expanded);
      if (next.has(action.payload)) next.delete(action.payload);
      else next.add(action.payload);
      return { ...state, expanded: next };
    }

    case 'DIFF_FETCH_START': {
      const next = new Set(state.diffLoadingByNumber);
      next.add(action.payload);
      return { ...state, diffLoadingByNumber: next };
    }

    case 'DIFF_FETCH_SUCCESS': {
      const { versionNumber, record } = action.payload;
      const nextLoading = new Set(state.diffLoadingByNumber);
      nextLoading.delete(versionNumber);
      // Splice the freshly-fetched record into both the list (so
      // re-renders see the populated diff/snapshot) and the index.
      const nextVersions = state.versions.map((v) =>
        v.version_number === versionNumber ? record : v,
      );
      return {
        ...withVersions(state, nextVersions),
        diffLoadingByNumber: nextLoading,
      };
    }

    case 'DIFF_FETCH_ERROR': {
      const next = new Set(state.diffLoadingByNumber);
      next.delete(action.payload);
      return { ...state, diffLoadingByNumber: next };
    }

    case 'OPEN_RESTORE_CONFIRM':
      return { ...state, pendingRestore: action.payload };

    case 'CLOSE_RESTORE_CONFIRM':
      return { ...state, pendingRestore: null };

    case 'RESTORE_START':
      return { ...state, restoring: true };

    case 'RESTORE_END':
      return { ...state, restoring: false, pendingRestore: null };

    case 'RESET':
      return initialVersionsState;

    default:
      return state;
  }
}
